import { c as _c } from "react/compiler-runtime";
import { postEntitiesByFpIdVaultDecrypt } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { getLogger } from '@onefootprint/logger';
import type { DataIdentifier, Document, DocumentKind, Entity } from '@onefootprint/request-types/dashboard';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import type { VaultType } from '@/entities/hooks/use-entity-vault';
import useEntityId from './use-entity-id';
const {
  logError
} = getLogger({
  location: 'use-decrypt-controls'
});
type DecryptStep = 'idle' | 'selecting-fields' | 'selecting-reason';
type DecryptControlsStoreState = {
  step: DecryptStep;
  isDecrypting: boolean;
  fields: DataIdentifier[];
  // Only track decrypted fields for the current entity (cleared when entity changes)
  currentEntityId: string | null;
  decryptedFields: DataIdentifier[];
  decryptedDocumentKinds: `${DocumentKind}-${number}`[];
};
const initialState: DecryptControlsStoreState = {
  step: 'idle',
  isDecrypting: false,
  fields: [],
  currentEntityId: null,
  decryptedFields: [],
  decryptedDocumentKinds: []
};
const useDecryptControlsStore = create<DecryptControlsStoreState>(() => initialState);
const useDecryptControls = () => {
  const $ = _c(26);
  const store = useDecryptControlsStore();
  const set = useDecryptControlsStore.setState;
  const get = useDecryptControlsStore.getState;
  const [isDecrypting, setIsDecrypting] = useState(false);
  const showErrorToast = useRequestErrorToast();
  const entityId = useEntityId();
  let t0;
  let t1;
  if ($[0] !== entityId || $[1] !== store.currentEntityId) {
    t0 = () => {
      if (entityId && store.currentEntityId !== entityId) {
        set({
          currentEntityId: entityId,
          decryptedFields: [],
          decryptedDocumentKinds: []
        });
      }
    };
    t1 = [entityId, store.currentEntityId, set];
    $[0] = entityId;
    $[1] = store.currentEntityId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== entityId || $[5] !== store.currentEntityId || $[6] !== store.decryptedFields) {
    t2 = store.currentEntityId === entityId ? store.decryptedFields : [];
    $[4] = entityId;
    $[5] = store.currentEntityId;
    $[6] = store.decryptedFields;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const decryptedFields = t2;
  let t3;
  if ($[8] !== entityId || $[9] !== store.currentEntityId || $[10] !== store.decryptedDocumentKinds) {
    t3 = store.currentEntityId === entityId ? store.decryptedDocumentKinds : [];
    $[8] = entityId;
    $[9] = store.currentEntityId;
    $[10] = store.decryptedDocumentKinds;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const decryptedDocumentKinds = t3;
  const defer = _temp2;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      set({
        step: "selecting-fields"
      });
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const startToSelectFields = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = async () => {
      const documentKinds = Array.from(document.querySelectorAll("input[name=\"documentKinds\"]"));
      const fields = Array.from(document.querySelectorAll("input[name=\"fields\"]"));
      fields.forEach(_temp3);
      for (const checkbox_0 of documentKinds) {
        checkbox_0.click();
        await defer();
      }
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const selectAllFields = t5;
  let selectReason;
  let startToDecryptAll;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    startToDecryptAll = async () => {
      await selectAllFields();
      selectReason();
    };
    selectReason = async () => {
      set({
        step: "selecting-reason"
      });
    };
    t6 = () => {
      set({
        step: "idle"
      });
    };
    $[14] = selectReason;
    $[15] = startToDecryptAll;
    $[16] = t6;
  } else {
    selectReason = $[14];
    startToDecryptAll = $[15];
    t6 = $[16];
  }
  const cancel = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      set(initialState);
      setIsDecrypting(false);
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const reset = t7;
  const getDisFromDocuments = _temp6;
  const getDocumentKindsFromFields = _temp9;
  let t8;
  if ($[18] !== showErrorToast) {
    t8 = async t9 => {
      const {
        entity,
        fields: fields_1,
        reason
      } = t9;
      const entityId_0 = entity.id;
      const state = get();
      const currentDecryptedFields = state.currentEntityId === entityId_0 ? state.decryptedFields : [];
      const currentDecryptedDocumentKinds = state.currentEntityId === entityId_0 ? state.decryptedDocumentKinds : [];
      const fieldsToDecrypt = fields_1.filter(field_1 => !currentDecryptedFields.includes(field_1));
      const hasUsTaxId = entity.data.some(_temp10);
      const hasSsnOrItin = fieldsToDecrypt.some(_temp11);
      if (hasUsTaxId && hasSsnOrItin && !fieldsToDecrypt.includes("id.us_tax_id")) {
        fieldsToDecrypt.push("id.us_tax_id");
      }
      setIsDecrypting(true);
      ;
      try {
        const documentKindsToDecrypt = getDocumentKindsFromFields(fieldsToDecrypt).filter(kind_1 => !currentDecryptedDocumentKinds.includes(kind_1));
        const response = await postEntitiesByFpIdVaultDecrypt({
          path: {
            fpId: entityId_0
          },
          body: {
            fields: fieldsToDecrypt,
            reason
          }
        });
        set(state_0 => ({
          step: "idle",
          currentEntityId: entityId_0,
          decryptedFields: state_0.currentEntityId === entityId_0 ? [...state_0.decryptedFields, ...fieldsToDecrypt] : fieldsToDecrypt,
          decryptedDocumentKinds: state_0.currentEntityId === entityId_0 ? [...state_0.decryptedDocumentKinds, ...documentKindsToDecrypt] : documentKindsToDecrypt
        }));
        setIsDecrypting(false);
        return response.data;
      } catch (t10) {
        const error = t10;
        logError("Vault decryption error", error, {
          entityId: entityId_0,
          fields: fieldsToDecrypt.join(",")
        });
        set({
          step: "selecting-reason"
        });
        setIsDecrypting(false);
        showErrorToast(error);
        return null;
      }
    };
    $[18] = showErrorToast;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const decrypt = t8;
  let t9;
  if ($[20] !== decrypt || $[21] !== decryptedDocumentKinds || $[22] !== decryptedFields || $[23] !== isDecrypting || $[24] !== store) {
    t9 = {
      ...store,
      decryptedFields,
      decryptedDocumentKinds,
      isDecrypting,
      startToSelectFields,
      selectAllFields,
      startToDecryptAll,
      selectReason,
      cancel,
      reset,
      getDisFromDocuments,
      decrypt
    };
    $[20] = decrypt;
    $[21] = decryptedDocumentKinds;
    $[22] = decryptedFields;
    $[23] = isDecrypting;
    $[24] = store;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export default useDecryptControls;
function _temp(resolve) {
  return setTimeout(resolve, 0);
}
function _temp2() {
  return new Promise(_temp);
}
function _temp3(checkbox) {
  checkbox.click();
}
function _temp4(upload) {
  return `${upload.identifier}:${upload.version}` as DataIdentifier;
}
function _temp5(d) {
  return d.uploads.map(_temp4);
}
function _temp6(documents, vaultData) {
  const imageDis = documents.flatMap(_temp5);
  const completedVersionDis = documents.flatMap(t0 => {
    const {
      completedVersion,
      kind,
      sambaActivityHistoryCompletedVersion
    } = t0;
    if (!completedVersion) {
      return [];
    }
    const documentDataKinds = Object.keys(vaultData.dataKinds).filter(key => key.startsWith(`document.${kind}`));
    return documentDataKinds.map(di => {
      const isSambaActivityDI = di.includes("samba_activity_history_response");
      const useSambaVersion = isSambaActivityDI && sambaActivityHistoryCompletedVersion;
      const version = useSambaVersion ? sambaActivityHistoryCompletedVersion : completedVersion;
      return `${di}:${version}` as DataIdentifier;
    });
  });
  return [...new Set([...imageDis, ...completedVersionDis])];
}
function _temp7(field) {
  return field.startsWith("document.");
}
function _temp8(field_0) {
  const parts = field_0.split(".");
  const kind_0 = parts[1];
  const version_0 = field_0.split(":")[1];
  return `${kind_0}-${version_0}` as `${DocumentKind}-${number}`;
}
function _temp9(fields_0) {
  const documentKinds_0 = fields_0.filter(_temp7).map(_temp8);
  return [...new Set(documentKinds_0)];
}
function _temp10(attr) {
  return attr.identifier === "id.us_tax_id";
}
function _temp11(field_2) {
  return field_2 === "id.ssn9" || field_2 === "id.itin";
}